import { useCallback, useContext, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import { ISurveyQuestion, ISurveyQuestionGroup, ISurveyQuestionType } from '../../../../shared/services/survey/survey.model';
import { AddGroupItemDialog } from '../../../dialogs/surveys/add-group-item-dialog';
import { SurveyQuestionDialog } from '../../../dialogs/surveys/survey-question-dialog';
import { SurveyQuestionsPanel } from '../../../presentation-components/surveys/builder/survey-questions-panel';
import { sortItemsByPage } from '../../../../shared/utils/survey-rules/group-pages';
import { SharedSetBanner } from './shared-set-banner';
import { SurveyHubTabProps } from '../survey-hub-page';

/** Chooser shown for older surveys created without a question set. */
const QuestionSetChooser = ({ surveyName, surveyId, reload }: { surveyName: string; surveyId: string; reload: () => void }) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [existingSets, setExistingSets] = useState<ISurveyQuestionGroup[]>([]);
  const [chosenSet, setChosenSet] = useState<ISurveyQuestionGroup | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    surveyAdminService.getGroups({ active: true })
      .then(data => setExistingSets(Array.isArray(data) ? data : ((data as { rows?: ISurveyQuestionGroup[] }).rows ?? [])))
      .catch(() => setExistingSets([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attachSet = async (groupId: string) => {
    setBusy(true);
    try {
      await surveyAdminService.updateSurvey(surveyId, { surveyQuestionGroupId: groupId });
      reload();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to attach the question set', backgroundColor: 'text.primary' });
    } finally {
      setBusy(false);
    }
  };

  const createBlankSet = async () => {
    setBusy(true);
    try {
      const group = await surveyAdminService.createGroup({ name: `${surveyName} questions` });
      await attachSet(group.id);
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to create a question set', backgroundColor: 'text.primary' });
      setBusy(false);
    }
  };

  return (
    <Paper variant='outlined' sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
        This survey has no questions yet
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Create a question set for this survey, or reuse one your organisation already has.
      </Typography>
      <Stack spacing={2}>
        <Box>
          <Button variant='contained' disabled={busy} onClick={createBlankSet}>
            Create question set
          </Button>
        </Box>
        {existingSets.length > 0 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Autocomplete
              options={existingSets}
              getOptionLabel={o => o.name}
              value={chosenSet}
              onChange={(_, v) => setChosenSet(v)}
              renderInput={params => <TextField {...params} size='small' label='Use an existing question set' />}
              sx={{ flex: 1, minWidth: 240 }}
            />
            <Button variant='outlined' disabled={busy || !chosenSet} onClick={() => chosenSet && attachSet(chosenSet.id)}>
              Use this set
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export const SurveyQuestionsTab = ({ survey, reload, isPartnerLocked }: SurveyHubTabProps) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [allQuestions, setAllQuestions] = useState<ISurveyQuestion[]>([]);
  const [questionTypes, setQuestionTypes] = useState<ISurveyQuestionType[]>([]);
  const [openAddItem, setOpenAddItem] = useState(false);
  const [openCreateQuestion, setOpenCreateQuestion] = useState(false);
  const [editQuestion, setEditQuestion] = useState<ISurveyQuestion | null>(null);
  const [addItemPage, setAddItemPage] = useState(1);

  const loadAllQuestions = useCallback(async () => {
    try {
      const result = await surveyAdminService.getQuestions({ active: true, perPage: 200 });
      setAllQuestions(result.rows);
    } catch {
      setAllQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAllQuestions(); }, [loadAllQuestions]);

  useEffect(() => {
    surveyAdminService.getQuestionTypes()
      .then(setQuestionTypes)
      .catch(() => setQuestionTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A question written here is added to this survey the moment it is created; the
  // library exists so questions can be reused, not as a detour on the way to one survey.
  const handleQuestionCreated = async (savedQuestionId?: string) => {
    setOpenCreateQuestion(false);
    loadAllQuestions();
    if (!savedQuestionId || !survey.surveyQuestionGroupId) { reload(); return; }
    try {
      await surveyAdminService.addGroupItem(survey.surveyQuestionGroupId, {
        surveyQuestionId: savedQuestionId,
        sortOrder: (survey.surveyQuestionGroup?.surveyQuestionGroupItems ?? []).length,
        pageNumber: addItemPage,
      });
    } catch {
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'The question was created but could not be added to this survey. Add it from the library.',
        backgroundColor: 'text.primary',
      });
    }
    reload();
  };

  const group = survey.surveyQuestionGroup;
  const groupId = survey.surveyQuestionGroupId;

  if (!groupId || !group) {
    return <QuestionSetChooser surveyName={survey.name} surveyId={survey.id} reload={reload} />;
  }

  const items = group.surveyQuestionGroupItems ?? [];
  const sortedItems = sortItemsByPage(items);
  const existingQuestionIds = new Set(items.map(i => i.surveyQuestionId));
  const availableQuestions = allQuestions.filter(q => !existingQuestionIds.has(q.id));

  return (
    <Box>
      <SharedSetBanner groupId={groupId} />
      <SurveyQuestionsPanel
        groupId={groupId}
        sortedItems={sortedItems}
        locked={isPartnerLocked}
        onChanged={reload}
        onAddQuestion={pageNum => { setAddItemPage(pageNum); setOpenAddItem(true); }}
        onCreateQuestion={pageNum => { setAddItemPage(pageNum); setOpenCreateQuestion(true); }}
        onEditQuestion={item => {
          const question = item.surveyQuestion ?? allQuestions.find(q => q.id === item.surveyQuestionId) ?? null;
          if (question) setEditQuestion(question);
        }}
        allQuestions={allQuestions}
      />
      <AddGroupItemDialog
        open={openAddItem}
        onClose={() => setOpenAddItem(false)}
        successHandler={reload}
        groupId={groupId}
        availableQuestions={availableQuestions}
        nextSortOrder={items.length}
        targetPageNumber={addItemPage}
      />
      {openCreateQuestion && (
        <SurveyQuestionDialog
          open={openCreateQuestion}
          onClose={() => setOpenCreateQuestion(false)}
          successHandler={handleQuestionCreated}
          questionTypes={questionTypes}
        />
      )}
      {editQuestion && (
        <SurveyQuestionDialog
          open={Boolean(editQuestion)}
          onClose={() => setEditQuestion(null)}
          successHandler={() => { setEditQuestion(null); loadAllQuestions(); reload(); }}
          questionTypes={questionTypes}
          existingQuestion={editQuestion}
        />
      )}
    </Box>
  );
};
